import { useState, useMemo } from "react";
import { produce } from "immer";

type InitFunc = () => Dict<boolean>;
type Key = string | number;
export function useBoolMap(initialValues?: Dict<boolean> | InitFunc) {
  const [map, setMap] = useState(
    typeof initialValues === "function" ? initialValues : () => (initialValues ? { ...initialValues } : {})
  );

  const [setTrue, setFalse, toggle, merge, clear, remove, replace] = useMemo(() => {
    return [
      (key: Key | Key[]) => setMap((oldMap) => produceUpdatedMap(oldMap, key, false)),
      (key: Key | Key[]) => setMap((oldMap) => produceUpdatedMap(oldMap, key, true)),
      (key: Key | Key[], newVal?: boolean) => setMap((oldMap) => produceUpdatedMap(oldMap, key, newVal)),
      (map: Dict<boolean>) => setMap((oldMap) => ({ ...oldMap, ...map })),
      () => setMap({}),
      (key: Key) =>
        setMap((oldMap) => {
          const newMap = { ...oldMap };
          delete newMap[key];
          return newMap;
        }),
      (map: Dict<boolean>) => setMap(map),
    ];
  }, []);

  return { map, setTrue, setFalse, toggle, merge, remove, replace };
}

function produceUpdatedMap(oldMap: Dict<boolean>, key: Key | Key[], targetValue?: boolean): Dict<boolean> {
  const setter = targetValue !== undefined ? () => targetValue : (prevValue: boolean) => !prevValue;
  return produce(oldMap, (draft: Dict<boolean>) => {
    const items = Array.isArray(key) ? key : [key];
    items.forEach((i) => (draft[i] = setter(draft[i])));
  });
}
